import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

import { SimpleAudioState } from "@/containers/Audio/SimpleAudio";
import AudioPlayer from "@/containers/AudioPlayer";
import { useAudioState } from "@/containers/Episode/LocalAudioContext";
import EpisodeAudio from "@/containers/Episode/partials/EpisodeAudio";
import { Episode } from "@/types/Episode";
import { EpisodeV4 } from "@/types/MentionCard";

type GlobalAudioContextType = {
  register: (audio: AudioComponent) => void;
  unregister: () => void;
  audio: EpisodeAudio | null;
} & Partial<SimpleAudioState>;

const AudioPlayerContext = createContext<GlobalAudioContextType>(
  {} as GlobalAudioContextType
);

/**
 *
 * Global Audio Player
 * All of the mentions act as remote controls to this single instance of Howl.
 * To be able to have "playlist"-like functionality with the our sound snippets we're
 * using an internal function in Howl called sprites.
 *
 * We're using the mention.public_uuid to differentiate the sprites.
 * A sprite looks like this : {[spriteName]: [start in ms,length in ms]}
 *
 * This is quite a logic heavy component which uses timeouts and callbacks to be able to know
 * when we're automatically moving on to the next sprite in the playlist and when to stop.
 * Make sure to test all the quadzillion cases in the player when changing something.
 *
 */

type AudioComponent = {
  episode?: Episode | EpisodeV4;
  play: (time?: number) => void;
  pause: () => void;
};

const AudioPlayerContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [audioPlayer, setAudioPlayer] = useState<EpisodeAudio | null>(null);

  const state = useAudioState(audioPlayer, {
    playing: true,
    loading: true,
    currentTime: true,
    errored: true,
  });

  const register = useCallback((audio: AudioComponent) => {
    setAudioPlayer(audio as EpisodeAudio);
  }, []);

  const unregister = useCallback(() => {
    setAudioPlayer(null);
  }, []);

  return (
    <AudioPlayerContext.Provider
      value={{
        register,
        unregister,
        audio: audioPlayer,
        ...state,
      }}
    >
      {children}
      <AudioPlayer />
    </AudioPlayerContext.Provider>
  );
};
const useGlobalAudioPlayer = () => useContext(AudioPlayerContext);

export { AudioPlayerContextProvider, useGlobalAudioPlayer };
